/**
 * Calculator Directory Data Structure
 *
 * Centralized metadata for all calculators and specialized tools.
 * Used by Homepage tabs and CalculatorCard components.
 */

export interface CalculatorItem {
    id: string
    title: string
    description: string
    route: string
    category: 'concrete' | 'steel' | 'specialized'
    icon: string
    status?: 'beta' | 'coming-soon'
}

export const calculators: CalculatorItem[] = [
    // ==================== CONCRETE DESIGN ====================
    {
        id: 'conc-beam-design',
        title: 'Concrete Beam Design',
        description: 'Reinforced concrete beam design with flexure and shear checks',
        route: '/calculators/concrete/beam-design',
        category: 'concrete',
        icon: 'mdi-minus-thick',
        status: 'beta'
    },
    {
        id: 'conc-column-design',
        title: 'Concrete Column Design',
        description: 'Reinforced concrete column design with biaxial bending',
        route: '/calculators/concrete/column-design',
        category: 'concrete',
        icon: 'mdi-pillar',
        status: 'beta'
    },
    {
        id: 'slab-design',
        title: 'Slab Design',
        description: 'One-way and two-way slab design per IS 456',
        route: '/calculators/concrete/slab-design',
        category: 'concrete',
        icon: 'mdi-layers'
    },
    {
        id: 'crack-width',
        title: 'Crack Width Calculator',
        description: 'Serviceability checks for concrete members',
        route: '/calculators/concrete/crack-width',
        category: 'concrete',
        icon: 'mdi-arrow-expand-horizontal'
    },
    {
        id: 'footing-design',
        title: 'Footing Design',
        description: 'Isolated footing design with stability analysis',
        route: '/calculators/concrete/footing-design',
        category: 'concrete',
        icon: 'mdi-arrow-collapse-down',
        status: 'beta'
    },
    {
        id: 'retaining-wall',
        title: 'Retaining Wall Design',
        description: 'Cantilever and counterfort retaining wall design',
        route: '/calculators/concrete/retaining-wall',
        category: 'concrete',
        icon: 'mdi-angle-right'
    },

    // ==================== STEEL DESIGN ====================
    {
        id: 'base-plate',
        title: 'Base Plate Design',
        description: 'Column base plate connections with anchor bolt design',
        route: '/base-plate',
        category: 'steel',
        icon: 'mdi-border-all'
    },
    {
        id: 'steel-column',
        title: 'Steel Column Design',
        description: 'Column design with buckling analysis per IS 800',
        route: '/calculators/steel/column-design',
        category: 'steel',
        icon: 'mdi-hexagon-outline'
    },
    {
        id: 'purlin-design',
        title: 'Purlin Design',
        description: 'Cold-formed steel purlin design for industrial buildings',
        route: '/calculators/steel/purlin-design',
        category: 'steel',
        icon: 'mdi-warehouse'
    },
    {
        id: 'gantry-girder',
        title: 'Gantry Girder Design',
        description: 'Overhead crane gantry girder design with crane load analysis',
        route: '/calculators/steel/gantry-girder',
        category: 'steel',
        icon: 'mdi-crane'
    },
    // ==================== SPECIALIZED TOOLS ====================
    {
        id: 'is-code-lookup',
        title: 'IS Code Lookup Tool',
        description: 'Bilinear interpolation for IS 800 design tables (8a-d, 9a-d)',
        route: '/calculators/specialized/lookup',
        category: 'specialized',
        icon: 'mdi-table-search'
    },
    {
        id: 'staad-plate-section-mto',
        title: 'STAAD Procurement-Ready MTO',
        description: 'Structural steel BOQ with plate-wise breakdown from STAAD .std files',
        route: '/calculators/specialized/staad-plate-section-mto',
        category: 'specialized',
        icon: 'mdi-file-table-box-multiple-outline',
        status: 'beta'
    },
    {
        id: 'load-combinations',
        title: 'Load Combinations Generator',
        description: 'IS 800 / IS 456 / IS 1893 compliant load combination builder for gravity and seismic cases',
        route: '/calculators/specialized/load-combinations',
        category: 'specialized',
        icon: 'mdi-sigma',
        status: 'beta'
    },
    {
        id: 'peb-optimizer',
        title: 'PEB Optimizer',
        description: 'Iterative optimization of pre-engineered building rafter sections',
        route: '/calculators/specialized/peb-optimizer',
        category: 'specialized',
        icon: 'mdi-auto-fix',
        status: 'beta'
    }
]
